/**
 * Content statistics loaded from pre-computed JSON (generated by scripts/generate-content-stats.ts).
 *
 * This replaces the previous 230 static imports of individual lesson files,
 * eliminating ~1.2MB from the main bundle.
 */

import type { BookContentStats, TotalContentStats } from '../types/progress';
import contentStatsData from '../content/content-stats.json';

// Lesson metadata for UI display
export interface LessonMeta {
  id: string;
  lessonNumber: number;
  titleArabic: string;
  titleEnglish: string;
}

/**
 * Lesson data in the format needed for building the FIRe encompassing graph
 */
export interface LessonForGraph {
  id: string;
  book: number;
  lesson: number;
  vocabulary?: string[];
  grammarPoints?: string[];
  exercises: Array<{
    id: string;
    itemIds: string[];
  }>;
}

// --- Typed accessors over the pre-computed data ---

const data = contentStatsData as {
  bookStats: Record<string, { lessonCount: number; wordCount: number; grammarCount: number; exerciseCount: number }>;
  totalStats: { lessonCount: number; wordCount: number; grammarCount: number; exerciseCount: number };
  lessonMeta: Record<string, LessonMeta[]>;
  lessonWordIds: Record<string, string[]>;
  bookWordIds: Record<string, string[]>;
  lessonExerciseCounts: Record<string, number>;
  lessonIdsByBook: Record<string, string[]>;
  allLessonIds: string[];
  lessonGraph: LessonForGraph[];
};

/**
 * Get content statistics for a specific book
 */
export function getBookContentStats(bookNumber: number): BookContentStats {
  const stats = data.bookStats[String(bookNumber)];
  if (!stats) {
    return { bookNumber, lessonCount: 0, wordCount: 0, grammarCount: 0, exerciseCount: 0 };
  }
  return { bookNumber, ...stats };
}

/**
 * Get total content statistics across all books
 */
export function getTotalContentStats(): TotalContentStats {
  return { ...data.totalStats };
}

/**
 * Get all word IDs for a specific book
 */
export function getWordIdsForBook(bookNumber: number): string[] {
  return data.bookWordIds[String(bookNumber)] || [];
}

/**
 * Get all word IDs for a specific lesson
 */
export function getLessonWordIds(lessonId: string): string[] {
  return data.lessonWordIds[lessonId] || [];
}

/**
 * Get exercise count for a specific lesson
 */
export function getLessonExerciseCount(lessonId: string): number {
  return data.lessonExerciseCounts[lessonId] || 0;
}

/**
 * Get all lesson IDs for a specific book
 */
export function getLessonIdsForBook(bookNumber: number): string[] {
  return data.lessonIdsByBook[String(bookNumber)] || [];
}

/**
 * Get all lesson IDs across all books
 */
export function getAllLessonIds(): string[] {
  return [...data.allLessonIds];
}

/**
 * Extract book number from lesson ID (e.g., 'b1-l01' -> 1)
 */
export function getBookNumberFromLessonId(lessonId: string): number {
  return parseInt(lessonId.charAt(1), 10);
}

/**
 * Extract lesson ID from exercise ID (e.g., 'b1-l01-ex01' -> 'b1-l01')
 */
export function getLessonIdFromExerciseId(exerciseId: string): string {
  const parts = exerciseId.split('-');
  if (parts.length >= 2) {
    return `${parts[0]}-${parts[1]}`;
  }
  return exerciseId;
}

/**
 * Get lesson metadata for a specific book (for Dashboard/UI display)
 */
export function getLessonMetaForBook(bookNumber: number): LessonMeta[] {
  return data.lessonMeta[String(bookNumber)] || [];
}

/**
 * Get all lesson metadata across all books
 */
export function getAllLessonMeta(): LessonMeta[] {
  return [
    ...getLessonMetaForBook(1),
    ...getLessonMetaForBook(2),
    ...getLessonMetaForBook(3),
  ];
}

/**
 * Get all lessons in the format needed for building the FIRe encompassing graph.
 * This includes vocabulary, grammar points, and exercise itemIds.
 */
export function getAllLessonsForGraph(): LessonForGraph[] {
  return data.lessonGraph;
}
